<template>
  <div class="component activity-cart-item">
    <!-- P2P only -->
    <div class="p2p" v-if="item.details.activityType === 'p2p'">
      <span class="from">{{ pickupLocation }}</span>
      <span class="to">{{ dropoffLocation }}</span>
    </div>
    <!-- P2P end -->

    <div class="date">{{ activityDate }}</div>

    <div class="prices">
      <ol class="price-breakdown">
        <li v-for="(price, index) in priceBreakdown" :key="index">
          <strong>{{ price.qty }}</strong> {{ price.label }} <span v-if="item.details.activityType !== 'p2p'">× {{ $formatter.currency(price.price) }}</span>
            <div v-if="price.addToInventory" class="options-label">{{ $t('general.label.includes') }}:</div>
            <ol v-if="price.addToInventory">
              <li v-if="price.mapToSeniors"><strong>{{ price.mapToSeniors }}</strong> {{ calculatePaxLabel('seniors', price.mapToSeniors) }}</li>
              <li v-if="price.mapToAdults"><strong>{{ price.mapToAdults }}</strong> {{ calculatePaxLabel('adults', price.mapToAdults) }}</li>
              <li v-if="price.mapToStudents"><strong>{{ price.mapToStudents }}</strong> {{ calculatePaxLabel('students', price.mapToStudents) }}</li>
              <li v-if="price.mapToChildren"><strong>{{ price.mapToChildren }}</strong> {{ calculatePaxLabel('children', price.mapToChildren) }}</li>
              <li v-if="price.mapToInfants"><strong>{{ price.mapToInfants }}</strong> {{ calculatePaxLabel('infants', price.mapToInfants) }}</li>
            </ol>
        </li>
      </ol>
      <div class="subtotal">{{ $formatter.currency(item.total) }}</div>
    </div>

    <dl class="details">
      <!-- Rental only -->
      <template v-if="item.details.activityType === 'rental'">
        <dt>{{ $t('general.label.pickup') }}:</dt>
        <dd>{{ rentalStart }}</dd>
        <dt>{{ $t('general.label.return') }}:</dt>
        <dd>{{ rentalEnd }}</dd>
      </template>
      <!-- Rental only end-->

      <template v-if="pickupLocation">
        <dt>{{ $t('general.label.pickup') }}:</dt>
        <dd>{{ pickupLocation }}<span v-if="item.pickupStartTime"> @ {{ formattedPickupTime }}</span></dd>
      </template>

      <template v-if="dropoffLocation">
        <dt>{{ $t('general.label.dropoff') }}:</dt>
        <dd>{{ dropoffLocation }}<span v-if="item.dropoffStartTime"> @ {{ formattedDropoffTime }}</span></dd>
      </template>

      <template v-for="(meal, mealIndex) in item.meals">
        <dt :key="`${mealIndex}-a`">{{ $t('form.label.meal') }}:</dt>
        <dd :key="`${mealIndex}-b`">{{ meal.name }}<span v-if="meal.notes">, {{ meal.notes }}</span></dd>
      </template>
    </dl>
  </div>
</template>

<script>
// import Cart from '@/models/Cart';
import moment from 'moment';

export default {
  props: ['item'],

  data() {
    return {
      paxPrefix: `activity${this.item.itemId}.`
    };
  },

  computed: {
    activityDate() {
      let activityDate = this.$formatter.date(this.item.date);
      let startTime = "";

      switch(this.item.details.activityType) {
        case 'regular':
          if (this.item.details.listStartTime) {
            startTime = this.item.details.listStartTime;
          } else {
            let filteredAvailability = this.item.details.filterByActiveDate(moment(this.item.date), this.item.details.availability);
            startTime = filteredAvailability ? filteredAvailability.startTime : "";
          }
          break;
        case 'rental':
          startTime = this.item.rentalStartTime;
          break;
        case 'p2p':
          startTime = "";
          break;
        case 'interval':
        default:
          startTime = this.item.startTime;
      }

      if(startTime) {
        activityDate += " @ " + this.$formatter.time(startTime);
      }

      return activityDate;
    },

    formattedPickupTime() {
      let pickupTime = this.$formatter.time(this.item.pickupStartTime);

      if(this.item.pickupStartTime != this.item.pickupEndTime) {
        pickupTime += " - " + this.$formatter.time(this.item.pickupEndTime);
      }

      return pickupTime;
    },

    formattedDropoffTime() {
      let dropoffTime = this.$formatter.time(this.item.dropoffStartTime);

      if(this.item.dropoffStartTime != this.item.dropoffEndTime) {
        dropoffTime += " - " + this.$formatter.time(this.item.dropoffEndTime);
      }

      return dropoffTime;
    },

    pickupLocation() {
      return this.getLocation('pickup');
    },

    dropoffLocation() {
      return this.getLocation('dropoff');
    },

    rentalStart() {
      return this.$formatter.date(this.item.date) + " @ " + this.$formatter.time(this.item.rentalStartTime);
    },

    rentalEnd() {
      let endDate = this.item.details.rentalIsMultiDay ? this.$formatter.date(this.item.rentalEndDate) : this.$formatter.date(this.item.date);

      return endDate + " @ " + this.$formatter.time(this.item.rentalEndTime);
    },

    priceBreakdown() {
      return this.item.activityPriceBreakdown.map(bd => {
        let label = bd.label;

        if (bd.type === 'pax') {
          label = this.calculatePaxLabel(bd.label, bd.qty);
        }

        return {
          qty: bd.qty,
          label: label,
          price: bd.unitPrice,
          addToInventory: bd.addToInventory,
          mapToSeniors: bd.qty * bd.mapToSeniors,
          mapToAdults: bd.qty * bd.mapToAdults,
          mapToStudents: bd.qty * bd.mapToStudents,
          mapToChildren: bd.qty * bd.mapToChildren,
          mapToInfants: bd.qty * bd.mapToInfants,
        };
      });
    }
  },

  methods: {
    getLocation(type) {
      let l = null;
      let loc = this.item[`${type}Location`];
      if (loc) {
        let obj = this.item.details[`${type}s`].find(item => item.id === loc);

        if (obj) {
          l = obj.name;

          let addr = this.item[`${type}Address`];
          if (addr) {
            l += `, ${addr}`;
          }
        }
      }
      return l;
    },

    calculatePaxLabel(pax, c = 2) {
      let key = `${this.paxPrefix}guest-picker.${pax}`;
      let label = this.$tc(key, c);

      if (key === label) {
        // we didn't find a label so fall back to no prefix
        label = this.$tc(`guest-picker.${pax}`, c);
      }

      return label;
    },
  }
}
</script>